// Berechnung & Chart-Konfiguration für den wöchentlichen Gewichtsverlauf.

export const BALKEN_BREITE = 56;
export const MIN_BREITE = 300;

export function berechneWochenDifferenzen(verlauf) {
    if (!Array.isArray(verlauf) || verlauf.length < 2) return [];

    const sorted = [...verlauf].sort(
        (a, b) => new Date(a.datum) - new Date(b.datum),
    );

    const getKW = (dateStr) => {
        const d = new Date(dateStr);
        const jan4 = new Date(d.getFullYear(), 0, 4);
        const startOfWeek1 = new Date(jan4);
        startOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
        const diff = d - startOfWeek1;
        const kw = Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1;
        return `KW ${String(kw).padStart(2, '0')} ${d.getFullYear()}`;
    };

    const gruppen = {};
    for (const eintrag of sorted) {
        const kw = getKW(eintrag.datum);
        if (!gruppen[kw]) gruppen[kw] = [];
        gruppen[kw].push(Number(eintrag.gewicht));
    }

    return Object.entries(gruppen)
        .filter(([, gewichte]) => gewichte.length >= 2)
        .map(([kw, gewichte]) => {
            const diff = +(gewichte[gewichte.length - 1] - gewichte[0]).toFixed(2);
            return { kw, diff };
        });
}

export function buildChartData(verlauf) {
    return {
        labels: verlauf.map((w) => w.kw),
        datasets: [
            {
                data: verlauf.map((w) => w.diff),
                // Pro Balken Farbe: grün positiv, rot negativ
                backgroundColor: verlauf.map((w) =>
                    w.diff > 0
                        ? 'rgba(34, 197, 94, 0.75)'
                        : w.diff < 0
                        ? 'rgba(239, 68, 68, 0.75)'
                        : 'rgba(71, 85, 105, 0.75)',
                ),
                borderColor: verlauf.map((w) =>
                    w.diff > 0
                        ? '#22c55e'
                        : w.diff < 0
                        ? '#ef4444'
                        : '#475569',
                ),
                borderWidth: 1.5,
                borderRadius: 4,
                // Balken wachsen immer von der 0-Linie weg
                borderSkipped: false,
            },
        ],
    };
}

export function buildChartOptions(maxAbs) {
    return {
        responsive: false, // wir steuern die Breite selbst (scrollbar)
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                borderColor: 'rgba(59, 130, 246, 0.3)',
                borderWidth: 1,
                titleColor: '#93c5fd',
                bodyColor: '#e0f2fe',
                bodyFont: { weight: '600', size: 13 },
                padding: 10,
                cornerRadius: 10,
                callbacks: {
                    title: (items) => items[0].label,
                    label: (item) => {
                        const v = item.raw;
                        return `${v >= 0 ? '+' : ''}${v.toFixed(2)} kg`;
                    },
                },
            },
            datalabels: undefined,
        },
        scales: {
            x: {
                grid: { color: 'rgba(59, 130, 246, 0.08)' },
                ticks: {
                    color: '#64748b',
                    font: { size: 11 },
                    maxRotation: 35,
                    minRotation: 35,
                },
                border: { color: '#334155' },
            },
            y: {
                min: -maxAbs,
                max: maxAbs,
                grid: {
                    color: (ctx) =>
                        ctx.tick.value === 0
                            ? 'rgba(148, 163, 184, 0.5)'
                            : 'rgba(59, 130, 246, 0.08)',
                    lineWidth: (ctx) => (ctx.tick.value === 0 ? 1.5 : 1),
                },
                ticks: {
                    color: '#64748b',
                    font: { size: 11 },
                    stepSize: 1,
                    callback: (v) => `${v > 0 ? '+' : ''}${v}`,
                },
                border: { color: '#334155' },
            },
        },
        animation: { duration: 400 },
    };
}

// Chart.js-Plugin: Wert außerhalb des Balkens anzeigen
// (positiv → über dem Balken, negativ → unter dem Balken)
export const datalabelPlugin = {
    id: 'outsideLabels',
    afterDatasetsDraw(chart) {
        const { ctx, data } = chart;
        const dataset = data.datasets[0];
        const meta = chart.getDatasetMeta(0);

        ctx.save();
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((bar, i) => {
            const value = dataset.data[i];
            if (value === 0) return;

            const label = `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
            const x = bar.x;
            const PADDING = 8;

            if (value >= 0) {
                // Über dem Balken
                ctx.fillStyle = '#22c55e';
                ctx.fillText(label, x, bar.y - PADDING);
            } else {
                // Unter dem Balken
                ctx.fillStyle = '#ef4444';
                ctx.fillText(label, x, bar.y + bar.height + PADDING);
            }
        });

        ctx.restore();
    },
};
